import messageIdMapping from "../IdMappings/MessageIdMapping.ts";
import {MessageNode} from "./MessageNode.ts";

type MessageMapping = Record<string, MessageNode>;

const getNodeTimestamp = (node: MessageNode) => {
    const timestamp = node.metadata?.timestamp;
    if (!timestamp) return 0;
    return typeof timestamp === "number" ? timestamp : Number(timestamp) || 0;
};

const inferPlaceholderRole = (parentRole: string | null) => {
    if (parentRole === "user") return "assistant";
    if (parentRole === "assistant") return "user";
    return "assistant";
};

const createPlaceholderNode = (
    id: string,
    parentId: string | null,
    childNum: number | null,
    role: string,
    mapping: MessageMapping
) => {
    const node = new MessageNode(
        id,
        childNum,
        {
            role,
            content: [{type: "text", text: "..."}],
            isCorruptedPlaceholder: true
        },
        [],
        parentId,
        {
            timestamp: Date.now(),
            isCorruptedPlaceholder: true,
            recoveredFromCorruption: true
        }
    );
    mapping[id] = node;
    return node;
};

const createRootNode = (id: string, mapping: MessageMapping) => {
    const node = new MessageNode(
        id,
        null,
        null,
        [],
        null,
        {
            timestamp: 0,
            isCorruptedPlaceholder: true,
            recoveredFromCorruption: true
        }
    );
    mapping[id] = node;
    return node;
};

const repairMissingParents = (mapping: MessageMapping) => {
    const childrenByMissingParent = new Map<string, MessageNode[]>();

    Object.values(mapping).forEach((node) => {
        if (!node.parent || mapping[node.parent]) return;

        if (!childrenByMissingParent.has(node.parent)) {
            childrenByMissingParent.set(node.parent, []);
        }
        childrenByMissingParent.get(node.parent)!.push(node);
    });

    childrenByMissingParent.forEach((children, parentId) => {
        const shouldBeRoot = children.every((child) => child.message?.role === "user");
        if (shouldBeRoot) {
            createRootNode(parentId, mapping);
        } else {
            createPlaceholderNode(parentId, null, null, inferPlaceholderRole(children[0]?.message?.role ?? null), mapping);
        }
    });
};

const repairParentChildren = (mapping: MessageMapping) => {
    const childNodesByParent = new Map<string, MessageNode[]>();

    Object.values(mapping).forEach((node) => {
        if (!node.parent) return;

        if (!childNodesByParent.has(node.parent)) {
            childNodesByParent.set(node.parent, []);
        }
        childNodesByParent.get(node.parent)!.push(node);
    });

    Object.values(mapping).forEach((parentNode) => {
        const linkedChildren = childNodesByParent.get(parentNode.id!) ?? [];
        const uniqueLinkedChildren = Array.from(new Map(linkedChildren.map((node) => [node.id, node])).values())
            .sort((a, b) => getNodeTimestamp(a) - getNodeTimestamp(b));

        const positionedChildren = new Map<number, MessageNode>();
        const unpositionedChildren: MessageNode[] = [];

        uniqueLinkedChildren.forEach((childNode) => {
            if (typeof childNode.childNum === "number" && childNode.childNum >= 0 && !positionedChildren.has(childNode.childNum)) {
                positionedChildren.set(childNode.childNum, childNode);
            } else {
                unpositionedChildren.push(childNode);
            }
        });

        const storedChildren = Array.isArray(parentNode.children) ? [...parentNode.children] : [];
        storedChildren.forEach((childId, index) => {
            let childNode = mapping[childId];
            if (!childNode) {
                childNode = createPlaceholderNode(childId, parentNode.id, index, inferPlaceholderRole(parentNode.message?.role ?? null), mapping);
            }

            childNode.parent = parentNode.id;
            if (typeof childNode.childNum !== "number") {
                childNode.childNum = index;
            }

            if (!positionedChildren.has(index)) {
                positionedChildren.set(index, childNode);
            }
        });

        const maxIndex = Math.max(storedChildren.length - 1, ...Array.from(positionedChildren.keys()), -1);
        const rebuiltChildren: string[] = [];

        for (let index = 0; index <= maxIndex; index++) {
            let childNode = positionedChildren.get(index);
            if (!childNode) {
                childNode = createPlaceholderNode(
                    `recovered-${parentNode.id}-${index}`,
                    parentNode.id,
                    index,
                    inferPlaceholderRole(parentNode.message?.role ?? null),
                    mapping
                );
            }

            childNode.parent = parentNode.id;
            childNode.childNum = index;
            rebuiltChildren.push(childNode.id!);
        }

        unpositionedChildren.forEach((childNode) => {
            const nextIndex = rebuiltChildren.length;
            childNode.parent = parentNode.id;
            childNode.childNum = nextIndex;
            rebuiltChildren.push(childNode.id!);
        });

        parentNode.children = rebuiltChildren;
    });
};

const getRootNode = (mapping: MessageMapping) => {
    const roots = Object.values(mapping).filter((node) => !node.parent);
    if (roots.length === 0) return null;

    return roots.sort((a, b) => {
        const aIsNullRoot = a.message === null ? 0 : 1;
        const bIsNullRoot = b.message === null ? 0 : 1;
        if (aIsNullRoot !== bIsNullRoot) return aIsNullRoot - bIsNullRoot;
        return getNodeTimestamp(a) - getNodeTimestamp(b);
    })[0];
};

const repairMessagesSequence = (rootNode: MessageNode | null, messagesSequence: number[], mapping: MessageMapping) => {
    if (!rootNode) return Array.isArray(messagesSequence) ? [...messagesSequence] : [];

    const repairedSequence = Array.isArray(messagesSequence) ? [...messagesSequence] : [];
    let currentNode = rootNode;

    repairedSequence.forEach((childIndex, depth) => {
        if (typeof childIndex !== "number" || childIndex < 0) return;

        while (currentNode.children.length <= childIndex) {
            const nextIndex = currentNode.children.length;
            const childNode = createPlaceholderNode(
                `recovered-${currentNode.id}-${depth}-${nextIndex}`,
                currentNode.id,
                nextIndex,
                inferPlaceholderRole(currentNode.message?.role ?? null),
                mapping
            );
            currentNode.children.push(childNode.id!);
        }

        const nextChildId = currentNode.children[childIndex];
        let nextNode = mapping[nextChildId];
        if (!nextNode) {
            nextNode = createPlaceholderNode(
                nextChildId,
                currentNode.id,
                childIndex,
                inferPlaceholderRole(currentNode.message?.role ?? null),
                mapping
            );
            currentNode.children[childIndex] = nextNode.id!;
        }

        nextNode.parent = currentNode.id;
        nextNode.childNum = childIndex;
        currentNode = nextNode;
    });

    return repairedSequence;
};

export const reconcileChatDag = (messagesSequence: number[], mapping: MessageMapping = messageIdMapping) => {
    repairMissingParents(mapping);
    repairParentChildren(mapping);

    const rootNode = getRootNode(mapping);
    const repairedSequence = repairMessagesSequence(rootNode, messagesSequence, mapping);

    return {rootNode, messagesSequence: repairedSequence};
};
